import { useEffect, useState, type FormEvent } from "react";
import {
  ArrowRight,
  Building2,
  Check,
  Cloud,
  FilePenLine,
  LockKeyhole,
  LogIn,
  LogOut,
  Mail,
  MapPin,
  ShieldCheck,
  Users,
} from "lucide-react";
import {
  AccountShell,
  accountApi,
  organizationLabels,
  roleLabels,
  type Organization,
  type RealmSession,
} from "../../account";

type OrganizationDirectoryEntry = {
  id: string;
  name: string;
  organization_type: string;
  business_id: string | null;
  municipality: string;
  source_url: string;
  source_checked_at: string;
  status: "sourced" | "claim_pending";
};

const registrationSteps = [
  {
    icon: FilePenLine,
    title: "Luo organisaatiotunnus",
    text: "Oma sähköposti ja salasana organisaatiokäyttöön.",
  },
  {
    icon: Mail,
    title: "Ilmoita organisaatio",
    text: "Virallinen nimi ja yhteystiedot.",
  },
  {
    icon: ShieldCheck,
    title: "Platform varmentaa toimijan",
    text: "Identiteetti, edustusoikeus ja alueet tarkistetaan.",
  },
  {
    icon: Users,
    title: "Valitse palvelut",
    text: "Alueet, luvat ja maksut otetaan käyttöön työtilassa.",
  },
];

export function OrganizationAccessPage({
  registration = false,
}: {
  registration?: boolean;
}) {
  const [session, setSession] = useState<RealmSession | null>(null),
    [error, setError] = useState(""),
    [loading, setLoading] = useState(false),
    [directoryQuery, setDirectoryQuery] = useState(""),
    [directoryLoading, setDirectoryLoading] = useState(false),
    [directoryEntries, setDirectoryEntries] = useState<
      OrganizationDirectoryEntry[]
    >([]),
    [selectedDirectory, setSelectedDirectory] =
      useState<OrganizationDirectoryEntry | null>(null);

  useEffect(() => {
    accountApi<RealmSession>("/api/account/session")
      .then(setSession)
      .catch((cause) => {
        setError(
          cause instanceof Error
            ? cause.message
            : "Istuntoa ei voitu tarkistaa.",
        );
        setSession({ authenticated: false });
      });
  }, []);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);
    setError("");
    const body: Record<string, FormDataEntryValue | string> = Object.fromEntries(
      new FormData(event.currentTarget).entries(),
    );
    if (selectedDirectory) {
      body.directoryEntryId = selectedDirectory.id;
      body.name = selectedDirectory.name;
      body.organizationType = selectedDirectory.organization_type;
      body.businessId =
        selectedDirectory.business_id ?? String(body.businessId ?? "");
    }
    try {
      const result = await accountApi<{ organization: Organization }>(
        "/api/account/organization-register",
        { method: "POST", body: JSON.stringify(body) },
      );
      window.location.assign(
        `/organisaatio/${encodeURIComponent(result.organization.slug)}`,
      );
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "Organisaatiotunnusta ei voitu luoda.",
      );
      setLoading(false);
    }
  };

  const searchDirectory = async () => {
    if (directoryQuery.trim().length < 2) return;
    setDirectoryLoading(true);
    setError("");
    try {
      const result = await accountApi<{ entries: OrganizationDirectoryEntry[] }>(
        `/api/public/organization-directory?q=${encodeURIComponent(directoryQuery.trim())}`,
      );
      setDirectoryEntries(result.entries);
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "Organisaatiohakua ei voitu tehdä.",
      );
    } finally {
      setDirectoryLoading(false);
    }
  };

  const logout = async () => {
    await accountApi("/api/account/logout", { method: "POST" });
    window.location.assign("/organisaatioille");
  };
  const organizationSession =
    session?.authenticated && session.accountType === "organization";

  return (
    <AccountShell
      signedIn={Boolean(session?.authenticated)}
      accountType="organization"
    >
      <main className="organization-onboarding organization-reference-layout">
        <section className="org-reference-hero">
          <div className="org-hero-copy">
            <span className="eyebrow">Organisaatioille</span>
            <h1>
              Organisaation oma
              <br />
              Lupava.fi-tunnus
            </h1>
            <p>
              Organisaatiotili on erillinen henkilökohtaisesta kalastustilistä.
              Sen kautta hallitaan organisaation tietoja, Core-kytkentöjä,
              lupatuotteita, jäseniä ja myöhemmin maksuja.
            </p>
          </div>
          <aside className="org-privacy-card">
            <div className="org-card-heading">
              <span>
                <Building2 size={28} />
              </span>
              <div>
                <small>Erillinen käyttöoikeusalue</small>
                <b>Ei henkilökohtaisia kalastus- tai ostotietoja</b>
              </div>
            </div>
            <p>Täysi tietosuoja organisaatiolle.</p>
            <ul>
              <li><Check size={15} /> Henkilökohtaiset tiedot erillään</li>
              <li><Check size={15} /> Vain organisaation tiedot näkyvissä</li>
              <li><Check size={15} /> Hallitse oikeuksia ja rooleja</li>
            </ul>
          </aside>
          <div className="org-trust-row">
            <span><ShieldCheck /> Turvallinen ja<br />luotettava</span>
            <span><LockKeyhole /> Tietosi säilyvät<br />suojattuina</span>
            <span><Cloud /> Pilvipalvelu – käytettävissä<br />missä vain</span>
            <span><Users /> Helppo hallinta ja<br />ajantasaisuus</span>
          </div>
        </section>

        <section className="org-reference-steps" aria-label="Organisaatiotilin vaiheet">
          {registrationSteps.map((item, index) => {
            const Icon = item.icon;
            return (
              <article key={item.title}>
                <span className="step-illustration">
                  <Icon size={35} />
                  <i>{index + 1}</i>
                </span>
                <div><b>{item.title}</b><p>{item.text}</p></div>
                {index < registrationSteps.length - 1 && (
                  <ArrowRight className="step-arrow" size={22} />
                )}
              </article>
            );
          })}
        </section>

        {!session ? (
          <div className="account-loading">Tarkistetaan kirjautumista…</div>
        ) : organizationSession ? (
          <section className="account-panel organization-account-home">
            <div className="account-panel-heading">
              <div>
                <span className="eyebrow">Organisaatiotili</span>
                <h2>{session.user?.displayName}</h2>
                <p className="panel-copy">Valitse hallittava työtila.</p>
              </div>
              <span className="verification-state verified">
                <ShieldCheck size={16} /> Organisaatioistunto
              </span>
            </div>
            <div className="organization-grid">
              {session.organizations?.map((item) => (
                <a href={`/organisaatio/${item.slug}`} key={item.id}>
                  <Building2 />
                  <span>
                    <small>{organizationLabels[item.organization_type ?? ""] ?? "Organisaatio"}</small>
                    <b>{item.name}</b>
                    <em>
                      {roleLabels[item.role] ?? item.role} ·{" "}
                      {item.status === "active" ? "varmennettu" : "odottaa varmennusta"}
                    </em>
                  </span>
                  <ArrowRight />
                </a>
              ))}
            </div>
          </section>
        ) : session.authenticated ? (
          <section className="onboarding-gate org-session-gate">
            <div>
              <span className="eyebrow">Henkilötili on aktiivinen</span>
              <h2>Vaihda organisaatiotunnukseen</h2>
              <p>Henkilötililtä ei siirretä nimeä, lupia, paikkoja tai ostoksia organisaatiolle.</p>
            </div>
            <div className="onboarding-actions">
              <button className="primary-link" onClick={logout}>
                <LogOut size={17} /> Kirjaudu henkilötililtä ulos
              </button>
            </div>
          </section>
        ) : registration ? (
          <section className="account-panel organization-registration org-reference-registration">
            <div className="account-panel-heading">
              <div>
                <span className="eyebrow">Organisaation rekisteröinti</span>
                <h2>Luo tunnus ja varmennettava työtila</h2>
              </div>
              <a className="secondary-action" href="/kirjaudu?accountType=organization">
                Onko tunnus jo? Kirjaudu
              </a>
            </div>
            <section className="organization-directory-search">
              <div>
                <span className="eyebrow">1. Etsi ensin</span>
                <h3>Onko organisaatio jo hakemistossa?</h3>
                <p>Valitse lähteestä tunnistettu profiili, jotta samaa toimijaa ei luoda kahdesti.</p>
              </div>
              <div className="organization-directory-searchbar">
                <input
                  value={directoryQuery}
                  onChange={(event) => setDirectoryQuery(event.target.value)}
                  placeholder="Esim. Ylisotkamon kalaveden osakaskunta"
                />
                <button
                  type="button"
                  onClick={() => void searchDirectory()}
                  disabled={directoryLoading || directoryQuery.trim().length < 2}
                >
                  {directoryLoading ? "Haetaan…" : "Hae hakemistosta"}
                </button>
              </div>
              {directoryEntries.length > 0 && (
                <div className="organization-directory-results">
                  {directoryEntries.map((entry) => (
                    <button
                      type="button"
                      key={entry.id}
                      className={selectedDirectory?.id === entry.id ? "selected" : ""}
                      disabled={entry.status === "claim_pending"}
                      onClick={() => setSelectedDirectory(entry)}
                    >
                      <Building2 size={18} />
                      <span>
                        <b>{entry.name}</b>
                        <small>
                          {organizationLabels[entry.organization_type] ?? entry.organization_type}
                          {entry.municipality ? ` · ${entry.municipality}` : ""}
                        </small>
                      </span>
                      <em>{entry.status === "claim_pending" ? "Haltuunotto käsittelyssä" : "Valitse"}</em>
                    </button>
                  ))}
                </div>
              )}
              {directoryEntries.length === 0 &&
                directoryQuery.trim().length >= 2 &&
                !directoryLoading && (
                  <small>Jos oikeaa profiilia ei löydy, voit ilmoittaa uuden organisaation alla.</small>
                )}
              {selectedDirectory && (
                <div className="organization-directory-selection">
                  <ShieldCheck size={17} />
                  <span>
                    <b>Valittu: {selectedDirectory.name}</b>
                    <small>Haltuunotto tarkistetaan erikseen eikä anna alue- tai myyntioikeuksia.</small>
                  </span>
                  <button type="button" onClick={() => setSelectedDirectory(null)}>Vaihda</button>
                </div>
              )}
            </section>
            <form
              key={selectedDirectory?.id ?? "new-organization"}
              className="organization-onboarding-form"
              onSubmit={submit}
            >
              <label>
                Organisaation tyyppi
                <select
                  name="organizationType"
                  defaultValue={selectedDirectory?.organization_type ?? "shareholding_association"}
                  disabled={Boolean(selectedDirectory)}
                >
                  {Object.entries(organizationLabels).map(([value, label]) => (
                    <option value={value} key={value}>{label}</option>
                  ))}
                </select>
              </label>
              <label>
                Virallinen nimi
                <input
                  name="name"
                  minLength={2}
                  maxLength={200}
                  defaultValue={selectedDirectory?.name ?? ""}
                  readOnly={Boolean(selectedDirectory)}
                  required
                />
              </label>
              <label>
                Y-tunnus <small>valinnainen</small>
                <input
                  name="businessId"
                  placeholder="1234567-8"
                  pattern="[0-9]{7}-[0-9]"
                  defaultValue={selectedDirectory?.business_id ?? ""}
                  readOnly={Boolean(selectedDirectory?.business_id)}
                />
              </label>
              {selectedDirectory && (
                <>
                  <label className="wide-field">
                    Haltuunoton peruste
                    <textarea
                      name="authorityBasis"
                      maxLength={2000}
                      placeholder="Kerro asemasi organisaatiossa ja millä perusteella edustat sitä."
                      required
                    />
                  </label>
                  <label className="wide-field">
                    Edustusoikeuden lähde
                    <input name="authorityEvidenceUrl" type="url" placeholder="https://…" required />
                  </label>
                </>
              )}
              <label>
                Organisaation yhteyssähköposti
                <input name="contactEmail" type="email" required />
              </label>
              <div className="form-divider"><span>Organisaatiotunnuksen ylläpitäjä</span></div>
              <label>
                Ylläpitäjän nimi
                <input name="displayName" autoComplete="name" minLength={2} maxLength={80} required />
              </label>
              <label>
                Kirjautumissähköposti
                <input name="email" type="email" autoComplete="email" required />
              </label>
              <label className="wide-field">
                Salasana
                <input name="password" type="password" autoComplete="new-password" minLength={12} required />
              </label>
              {error && <p className="account-error">{error}</p>}
              <label className="onboarding-consent">
                <input type="checkbox" required />
                <span>Vakuutan, että minulla on oikeus rekisteröidä organisaatio. Tiedot tarkistetaan ennen julkaisua.</span>
              </label>
              <button disabled={loading}>
                {loading ? "Luodaan…" : "Luo organisaatiotunnus"}<ArrowRight />
              </button>
            </form>
          </section>
        ) : (
          <>
            <section className="org-reference-bottom">
              <article className="org-reference-cta">
                <div>
                  <span className="eyebrow">Organisaatiotili</span>
                  <h2>Kirjaudu tai rekisteröi organisaatio</h2>
                  <p>Tavallinen käyttäjätili ei sisällä organisaatioiden tietoja. Organisaatiolle luodaan oma tunnus ja varmennettava työtila.</p>
                  <div className="onboarding-actions">
                    <a className="primary-link" href="/kirjaudu?accountType=organization&next=%2Forganisaatioille">
                      <LogIn size={17} /> Organisaation kirjautuminen
                    </a>
                    <a className="secondary-link" href="/organisaatioille/rekisteroidy">
                      <Building2 size={17} /> Rekisteröi organisaatio
                    </a>
                  </div>
                  {error && <p className="account-error">{error}</p>}
                </div>
                <span className="org-building-art"><Building2 size={86} /></span>
              </article>
              <article className="org-stat">
                <span><Building2 /></span><strong>8</strong><b>organisaatiotyyppiä</b><small>Yksi selkeä työtila</small>
              </article>
              <article className="org-stat">
                <span><MapPin /></span><strong>3</strong><b>erillistä tarkistusrajaa</b><small>Identiteetti, edustus ja alue</small>
              </article>
              <article className="org-stat">
                <span><ShieldCheck /></span><strong>0 €</strong><b>ennen aktivointia</b><small>Ei automaattista veloitusta</small>
              </article>
            </section>
            <aside className="org-security-strip">
              <span><LockKeyhole size={18} /></span>
              <div>
                <b>Tietoturva etusijalla</b>
                <small>Noudatamme EU:n tietosuoja-asetusta (GDPR). Tietosi ovat turvassa.</small>
              </div>
              <a href="/#tietosuoja">Lue lisää tietoturvasta <ArrowRight size={14} /></a>
            </aside>
          </>
        )}
      </main>
    </AccountShell>
  );
}
